import {
  Accordion,
  Accordions,
  AccountAddressDetails,
  AccountDetails,
  AccountOrderList,
  AccountProductList,
  BlogPostCard,
  CollectionCarousel,
  Freeform,
  LoginForm,
  Newsletter,
  PasswordRecoveryForm,
  ProductBundleForm,
  ProductCard,
  SignupForm,
  Video,
} from '@data/sanity/schema'
import { SanityBlogPostWithoutBody, SanityBlogPostOptions } from './blog'
import { SanityContentFragment } from './content'
import { SanityProductBundle, SanityProductFragment } from './product'
import { HasAccountStrings, HasAuthStrings } from './site'
import { SanityVideo } from './video'
import { Keyed } from '@lib/helpers'

export enum SanityBlockType {
  ACCORDIONS = 'accordions',
  ACCOUNT_ADDRESS_DETAILS = 'accountAddressDetails',
  ACCOUNT_DETAILS = 'accountDetails',
  ACCOUNT_ORDER_LIST = 'accountOrderList',
  ACCOUNT_PRODUCT_LIST = 'accountProductList',
  BLOG_POST_CARD = 'blogPostCard',
  COLLECTION_CAROUSEL = 'collectionCarousel',
  FREEFORM = 'freeform',
  LOGIN_FORM = 'loginForm',
  NEWSLETTER = 'newsletter',
  PASSWORD_RECOVERY_FORM = 'passwordRecoveryForm',
  PRODUCT_BUNDLE_FORM = 'productBundleForm',
  PRODUCT_CARD = 'productCard',
  SIGNUP_FORM = 'signupForm',
  VIDEO = 'video',
}

export type SanityFreeformBlock = Keyed<
  Pick<Freeform, '_type' | 'textAlign' | 'maxWidth'> & {
    content: Array<SanityContentFragment>
  }
>

export type SanityFreeform = Omit<SanityFreeformBlock, '_type' | '_key'>

export type SanityAccordionsBlockItem = Pick<Accordion, 'title'> & {
  id: string
  content: Array<SanityContentFragment>
}

export type SanityAccordionsBlock = Keyed<
  Pick<Accordions, '_type'> & {
    items: Array<SanityAccordionsBlockItem>
  }
>

export type SanityAccordions = Omit<SanityAccordionsBlock, '_type' | '_key'>

export type SanityProductCardBlock = Keyed<
  Pick<ProductCard, '_type'> & {
    product: SanityProductFragment
  }
>

export type SanityProductCard = Omit<SanityProductCardBlock, '_type' | '_key'>

export type SanitySignupFormBlock = Keyed<SignupForm & HasAuthStrings>

export type SanityLoginFormBlock = Keyed<LoginForm & HasAuthStrings>

export type SanityPasswordRecoveryFormBlock = Keyed<
  PasswordRecoveryForm & HasAuthStrings
>

export type SanityAccountDetailsBlock = Keyed<
  AccountDetails & HasAccountStrings
>

export type SanityAccountAddressDetailsBlock = Keyed<
  AccountAddressDetails & HasAccountStrings
>

export type SanityAccountProductListBlock = Keyed<
  AccountProductList & HasAccountStrings
>

export type SanityAccountOrderListBlock = Keyed<
  AccountOrderList & HasAccountStrings
>

export type SanityVideoBlock = Keyed<Pick<Video, '_type'> & SanityVideo>

export type SanityCollectionCarouselBlock = Keyed<
  Pick<CollectionCarousel, '_type'> & {
    collection: {
      products: Array<SanityProductFragment>
    }
  }
>

export type SanityCollectionCarousel = Omit<
  SanityCollectionCarouselBlock,
  '_type' | '_key'
>

export type SanityNewsletterBlock = Keyed<
  Pick<Newsletter, '_type' | 'klaviyoListID' | 'submit'> & {
    successMsg: Array<SanityContentFragment>
    errorMsg: Array<SanityContentFragment>
    terms?: Array<SanityContentFragment>
  }
>

export type SanityProductBundleFormBlock = Keyed<
  Pick<ProductBundleForm, '_type' | 'showGallery'> & {
    productBundle: SanityProductBundle
  }
>

export type SanityBlogPostCardBlock = Keyed<
  Pick<BlogPostCard, '_type'> & {
    post: SanityBlogPostWithoutBody
    options?: SanityBlogPostOptions
  }
>

export type SanityBlocks =
  | SanityFreeformBlock
  | SanityAccordionsBlock
  | SanityProductCardBlock
  | SanitySignupFormBlock
  | SanityLoginFormBlock
  | SanityPasswordRecoveryFormBlock
  | SanityAccountDetailsBlock
  | SanityAccountAddressDetailsBlock
  | SanityAccountProductListBlock
  | SanityAccountOrderListBlock
  | SanityVideoBlock
  | SanityCollectionCarouselBlock
  | SanityNewsletterBlock
  | SanityProductBundleFormBlock
  | SanityBlogPostCardBlock
